import { useState } from 'react';
import {
  Box,
  Chip,
  CircularProgress,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Pagination,
  Paper,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';

import { useMfaBitacora } from '@/shared/actions/erp/administration/mfa-admin';

const EVENTOS = [
  '',
  'SETUP_INICIADO',
  'ACTIVADO',
  'DESACTIVADO',
  'LOGIN_OK',
  'LOGIN_FALLIDO',
  'LOGIN_BLOQUEADO',
  'USO_BACKUP',
  'REGENERAR_BACKUP',
  'ADMIN_RESET',
  'ADMIN_DESBLOQUEO',
  'ADMIN_REGEN_BACKUP',
  'ADMIN_FORZAR',
  'ADMIN_QUITAR_FORZAR',
  'ADMIN_GRACE',
  'POLITICA_CAMBIADA',
];

const colorEvento: Record<string, 'default' | 'success' | 'warning' | 'error' | 'info'> = {
  ACTIVADO: 'success',
  LOGIN_OK: 'success',
  DESACTIVADO: 'warning',
  LOGIN_FALLIDO: 'warning',
  LOGIN_BLOQUEADO: 'error',
  ADMIN_RESET: 'error',
  ADMIN_FORZAR: 'info',
  ADMIN_QUITAR_FORZAR: 'info',
  POLITICA_CAMBIADA: 'info',
};

export const MfaAdminBitacoraTab = () => {
  const [evento, setEvento] = useState('');
  const [usuarioId, setUsuarioId] = useState<string>('');
  const [page, setPage] = useState(1);

  const { data, isLoading, isFetching } = useMfaBitacora({
    evento: evento || undefined,
    user_id: usuarioId ? Number(usuarioId) : undefined,
    page,
    page_size: 50,
  });

  const filas = data?.data ?? [];
  const numPaginas = data?.numero_paginas ?? 1;

  return (
    <Stack spacing={2}>
      <Paper variant="outlined" sx={{ p: 2 }}>
        <Grid container spacing={2}>
          <Grid item xs={12} md={4}>
            <FormControl size="small" fullWidth>
              <InputLabel>Evento</InputLabel>
              <Select
                label="Evento"
                value={evento}
                onChange={(e) => {
                  setEvento(e.target.value);
                  setPage(1);
                }}
              >
                {EVENTOS.map((e) => (
                  <MenuItem key={e || 'todos'} value={e}>
                    {e || 'Todos'}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={4}>
            <TextField
              size="small"
              fullWidth
              label="ID de usuario (opcional)"
              type="number"
              value={usuarioId}
              onChange={(e) => {
                setUsuarioId(e.target.value);
                setPage(1);
              }}
            />
          </Grid>
          <Grid item xs={12} md={4} sx={{ textAlign: 'right' }}>
            <Typography variant="caption" color="text.secondary">
              {data?.count ?? 0} registro(s)
            </Typography>
          </Grid>
        </Grid>
      </Paper>

      <TableContainer component={Paper} variant="outlined">
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Fecha</TableCell>
              <TableCell>Usuario</TableCell>
              <TableCell>Evento</TableCell>
              <TableCell>Método</TableCell>
              <TableCell>Admin</TableCell>
              <TableCell>IP</TableCell>
              <TableCell>Detalle</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {isLoading || isFetching ? (
              <TableRow>
                <TableCell colSpan={7} align="center">
                  <CircularProgress size={24} />
                </TableCell>
              </TableRow>
            ) : filas.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} align="center">
                  <Typography variant="body2" color="text.secondary">
                    Sin eventos
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              filas.map((e) => (
                <TableRow key={e.id} hover>
                  <TableCell>
                    <Typography variant="caption">
                      {new Date(e.fecha_creacion).toLocaleString()}
                    </Typography>
                  </TableCell>
                  <TableCell>{e.usuario || '-'}</TableCell>
                  <TableCell>
                    <Chip
                      size="small"
                      label={e.evento}
                      color={colorEvento[e.evento] || 'default'}
                      variant={colorEvento[e.evento] ? 'filled' : 'outlined'}
                    />
                  </TableCell>
                  <TableCell>{e.metodo || '-'}</TableCell>
                  <TableCell>{e.admin || '-'}</TableCell>
                  <TableCell>
                    <Typography variant="caption" color="text.secondary">
                      {e.ip || '-'}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{
                        fontFamily: 'monospace',
                        whiteSpace: 'pre-wrap',
                        wordBreak: 'break-word',
                      }}
                    >
                      {e.detalle ? JSON.stringify(e.detalle) : '-'}
                    </Typography>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <Box display="flex" justifyContent="flex-end">
        <Pagination
          count={numPaginas}
          page={page}
          onChange={(_, p) => setPage(p)}
          size="small"
        />
      </Box>
    </Stack>
  );
};
